import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Star, ShieldCheck, Lock, MessageSquare, School as SchoolIcon, Package, Trophy } from "lucide-react";

function RoleBadge({ role }) {
  const map = {
    genitore: { label: "Genitore", bg: "#DCE4DC", color: "#1A362D" },
    professionista: { label: "Professionista", bg: "#FAF3E7", color: "#C05C45" },
  };
  const cfg = map[role] || { label: role, bg: "#E8E6E1", color: "#5C6B64" };
  return (
    <span
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-ui font-medium"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}
    >
      <ShieldCheck className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function StatBlock({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-[#E8E6E1] bg-white" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg bg-[#FAF3E7] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#C05C45]" />
      </div>
      <div>
        <p className="font-display text-2xl text-[#1A362D] leading-none">{value}</p>
        <p className="font-ui text-xs text-[#8A9691] mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function UserProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Privacy first: solo loggati
      navigate("/");
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [p, a] = await Promise.all([
          api.get(`/users/by-slug/${slug}`),
          api.get(`/users/by-slug/${slug}/activities`),
        ]);
        if (!active) return;
        setProfile(p.data);
        setActivities(a.data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug, user, authLoading, navigate]);

  if (authLoading || loading) {
    return <div className="max-w-5xl mx-auto px-4 py-20 font-ui text-[#5C6B64]" data-testid="user-profile-loading">Caricamento profilo…</div>;
  }
  if (notFound) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 text-center" data-testid="user-profile-notfound">
        <h1 className="font-display text-3xl text-[#1A362D]">Profilo non trovato</h1>
        <p className="mt-3 font-ui text-sm text-[#5C6B64]">Questo profilo non esiste o è stato rimosso.</p>
        <Link to="/" className="inline-block mt-6 btn-secondary text-sm">Torna alla home</Link>
      </div>
    );
  }
  if (!profile) return null;

  const initial = (profile.name || "?")[0];

  return (
    <article className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="user-profile-page">
      {/* Privacy disclaimer */}
      <div className="mb-6 flex items-center gap-2 rounded-lg bg-[#FAF3E7] border border-[#E8DCC4] px-4 py-3">
        <Lock className="w-4 h-4 text-[#C05C45] flex-shrink-0" />
        <p className="font-ui text-xs text-[#5C6B64]">
          I dati presenti in questa scheda sono visibili <strong>solo all&apos;interno di questa community</strong>.
        </p>
      </div>

      {/* Header card */}
      <section className="card-surface p-8 sm:p-10">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {profile.picture ? (
            <img
              src={profile.picture}
              alt={profile.name}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-[#E8E6E1]"
              data-testid="user-profile-avatar"
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#DCE4DC] flex items-center justify-center font-display text-4xl text-[#1A362D]">
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight" data-testid="user-profile-name">
              {profile.name}
            </h1>
            {(profile.roles || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="user-profile-roles">
                {profile.roles.map((r) => <RoleBadge key={r} role={r} />)}
              </div>
            )}
            {profile.bio ? (
              <p className="mt-5 font-ui text-base text-[#1A362D] leading-relaxed max-w-2xl" data-testid="user-profile-bio">
                &ldquo;{profile.bio}&rdquo;
              </p>
            ) : (
              <p className="mt-5 font-ui text-sm text-[#8A9691] italic">Nessuna descrizione personale.</p>
            )}
            {profile.is_self && (
              <Link
                to="/area-personale"
                data-testid="user-profile-edit-link"
                className="inline-block mt-5 font-ui text-xs text-[#C05C45] hover:underline"
              >
                Modifica il tuo profilo →
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      {activities && (
        <section className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="user-profile-stats">
          <StatBlock icon={MessageSquare} label="Esperienze" value={activities.stats.reviews_count} testid="stat-reviews" />
          <StatBlock icon={SchoolIcon} label="Scuole suggerite" value={activities.stats.school_suggestions_count} testid="stat-schools" />
          <StatBlock icon={Package} label="Prodotti suggeriti" value={activities.stats.product_suggestions_count} testid="stat-products" />
          <StatBlock icon={Trophy} label="Badge raggiunti" value={activities.stats.badges_count} testid="stat-badges" />
        </section>
      )}

      {/* Activities lists */}
      {activities && (
        <section className="mt-12 space-y-12">
          {/* Reviews */}
          <div data-testid="user-profile-reviews">
            <h2 className="font-display text-2xl text-[#1A362D]">Esperienze lasciate</h2>
            {activities.reviews.length === 0 ? (
              <p className="mt-3 font-ui text-sm text-[#8A9691]">Nessuna esperienza ancora.</p>
            ) : (
              <ul className="mt-5 space-y-4">
                {activities.reviews.map((r) => (
                  <li key={r.id} className="card-surface p-5">
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`w-4 h-4 ${n <= (r.rating || 0) ? "fill-[#E8B14F] text-[#E8B14F]" : "text-[#E8E6E1]"}`} />
                        ))}
                      </div>
                      {r.target_url && (
                        <Link to={r.target_url} className="ml-2 font-ui text-xs text-[#C05C45] hover:underline">
                          {r.target_label}
                        </Link>
                      )}
                    </div>
                    <p className="mt-3 font-ui text-sm text-[#1A362D] leading-relaxed whitespace-pre-line">{r.comment}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Suggestions */}
          {(activities.school_suggestions.length > 0 || activities.product_suggestions.length > 0) && (
            <div data-testid="user-profile-suggestions">
              <h2 className="font-display text-2xl text-[#1A362D]">Suggerimenti inviati</h2>
              <ul className="mt-5 space-y-3">
                {activities.school_suggestions.map((s) => (
                  <li key={s.id} className="card-surface p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-ui text-sm text-[#1A362D] truncate">
                        <SchoolIcon className="w-4 h-4 inline mr-1 text-[#5C6B64]" />
                        Scuola: {s.name || (s.data && s.data.name) || "—"}
                      </p>
                    </div>
                    <span className="font-ui text-xs text-[#8A9691] capitalize">{s.status || "pending"}</span>
                  </li>
                ))}
                {activities.product_suggestions.map((s) => (
                  <li key={s.id} className="card-surface p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-ui text-sm text-[#1A362D] truncate">
                        <Package className="w-4 h-4 inline mr-1 text-[#5C6B64]" />
                        Prodotto: {s.name}
                      </p>
                    </div>
                    <span className="font-ui text-xs text-[#8A9691] capitalize">{s.status || "pending"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Badges */}
          {activities.badges.length > 0 && (
            <div data-testid="user-profile-badges">
              <h2 className="font-display text-2xl text-[#1A362D]">Badge raggiunti</h2>
              <div className="mt-5 flex flex-wrap gap-3">
                {activities.badges.map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF3E7] border border-[#E8DCC4]">
                    <Trophy className="w-4 h-4 text-[#C05C45]" />
                    <span className="font-ui text-sm text-[#1A362D] capitalize">{b.badge_type}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </article>
  );
}
